import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { MATERIAL_PRESETS } from '@/lib/palettes/presets';
import { toast } from 'sonner';

export interface AdminRole {
  id: number;
  name: string;
  description?: string;
  permissions: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface AdminAuditLog {
  id: number;
  admin_id: string;
  action: string;
  table_name?: string;
  record_id?: string;
  old_values?: Record<string, any>;
  new_values?: Record<string, any>;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

export interface ColorPalette {
  id: number;
  name: string;
  description?: string;
  is_default: boolean;
  is_active: boolean;
  colors: Record<string, string>;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface UserSubscription {
  id: number;
  user_id: string;
  subscription_type: string;
  status: string;
  start_date: string;
  end_date?: string;
  payment_method?: string;
  amount?: number;
  currency: string;
  auto_renew: boolean;
  created_at: string;
  updated_at: string;
}

export interface SubscriptionPlan {
  id: number;
  name: string;
  description?: string;
  price: number;
  currency: string;
  billing_cycle: string;
  features: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export function useAdmin() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Admin role management
  const getAdminRoles = useCallback(async (): Promise<AdminRole[]> => {
    try {
      const { data, error } = await supabase
        .from('admin_roles')
        .select('*')
        .order('name');

      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Error fetching admin roles:', error);
      toast.error('Erro ao carregar papéis de administrador');
      return [];
    }
  }, []);

  // Color palette management
  const seedDefaultColorPalettesIfMissing = useCallback(async (): Promise<void> => {
    try {
      // fetch existing names once
      const { data: existing, error: fetchError } = await supabase
        .from('color_palettes')
        .select('name, is_default');

      if (fetchError) throw fetchError;

      const existingNames = new Set((existing || []).map((p: any) => p.name));

      const toInsert = MATERIAL_PRESETS
        .filter((preset) => !existingNames.has(preset.name))
        .map((preset) => ({
          name: preset.name,
          description: preset.description,
          is_default: preset.is_default,
          is_active: false,
          colors: preset.colors,
        }));

      if (toInsert.length > 0) {
        const { error: insertError } = await supabase.from('color_palettes').insert(toInsert);
        if (insertError) throw insertError;
      }

      // Ensure at most one default palette; prefer the first default in presets
      const defaults = (existing || []).filter((p: any) => p.is_default);
      const shouldNormalizeDefault = defaults.length > 1;
      if (shouldNormalizeDefault) {
        // Set all to false, then set the intended default (Material Blue) to true
        const { error: resetError } = await supabase
          .from('color_palettes')
          .update({ is_default: false })
          .neq('name', '');
        if (resetError) throw resetError;

        const { error: setDefaultError } = await supabase
          .from('color_palettes')
          .update({ is_default: true })
          .eq('name', 'Material Blue');
        if (setDefaultError) throw setDefaultError;
      }
    } catch (e) {
      console.error('Error seeding default palettes:', e);
      // do not toast here to avoid noisy admin page loads
    }
  }, []);
  const getColorPalettes = useCallback(async (): Promise<ColorPalette[]> => {
    try {
      // ensure presets exist
      await seedDefaultColorPalettesIfMissing();

      const { data, error } = await supabase
        .from('color_palettes')
        .select('*')
        .order('is_default', { ascending: false })
        .order('name');

      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Error fetching color palettes:', error);
      toast.error('Erro ao carregar paletas de cores');
      return [];
    }
  }, [seedDefaultColorPalettesIfMissing]);

  const setActiveColorPalette = useCallback(async (paletteId: number): Promise<boolean> => {
    try {
      setLoading(true);
      
      // First, deactivate all palettes
      const { error: deactivateError } = await supabase
        .from('color_palettes')
        .update({ is_active: false })
        .neq('id', paletteId);

      if (deactivateError) throw deactivateError;

      // Then activate the selected palette
      const { error: activateError } = await supabase
        .from('color_palettes')
        .update({ is_active: true })
        .eq('id', paletteId);

      if (activateError) throw activateError;

      toast.success('Paleta de cores atualizada com sucesso!');
      return true;
    } catch (error: any) {
      console.error('Error setting active color palette:', error);
      toast.error('Erro ao atualizar paleta de cores');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const createColorPalette = useCallback(async (palette: Omit<ColorPalette, 'id' | 'created_at' | 'updated_at'>): Promise<boolean> => {
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('color_palettes')
        .insert(palette);

      if (error) throw error;

      toast.success('Paleta de cores criada com sucesso!');
      return true;
    } catch (error: any) {
      console.error('Error creating color palette:', error);
      toast.error('Erro ao criar paleta de cores');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // User subscription management
  const getUserSubscriptions = useCallback(async (): Promise<UserSubscription[]> => {
    try {
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('*, subscription_plans(name, description, price, features)')
        .leftJoin('subscription_plans', 'user_subscriptions.subscription_type', 'subscription_plans.name')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Error fetching user subscriptions:', error);
      toast.error('Erro ao carregar assinaturas de usuários');
      return [];
    }
  }, []);

  const getSubscriptionPlans = useCallback(async (): Promise<SubscriptionPlan[]> => {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('price');

      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Error fetching subscription plans:', error);
      toast.error('Erro ao carregar planos de assinatura');
      return [];
    }
  }, []);

  const updateUserSubscription = useCallback(async (subscriptionId: number, updates: Partial<UserSubscription>): Promise<boolean> => {
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('user_subscriptions')
        .update(updates)
        .eq('id', subscriptionId);

      if (error) throw error;

      toast.success('Assinatura atualizada com sucesso!');
      return true;
    } catch (error: any) {
      console.error('Error updating user subscription:', error);
      toast.error('Erro ao atualizar assinatura');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Audit log management
  const getAuditLogs = useCallback(async (limit = 100): Promise<AdminAuditLog[]> => {
    try {
      const { data, error } = await supabase
        .from('admin_audit_log')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Error fetching audit logs:', error);
      toast.error('Erro ao carregar logs de auditoria');
      return [];
    }
  }, []);

  return {
    loading,
    error,
    getAdminRoles,
    getColorPalettes,
    setActiveColorPalette,
    createColorPalette,
    seedDefaultColorPalettesIfMissing,
    getUserSubscriptions,
    getSubscriptionPlans,
    updateUserSubscription,
    getAuditLogs
  };
}
